import {
  KeyboardEvent,
  MouseEvent,
  PointerEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useTheme } from "@mui/material";

import useLocalStorage from "hooks/useLocalStorage.hook";

import {
  AssistantDragHandlers,
  IAssistantDragArgs,
  IAssistantDragStart,
  IAssistantPosition,
} from "../types";
import { clampAssistantPosition, isAssistantPosition } from "../utils";

export function useAssistantDrag({
  elementRef,
  visible,
  storageKey,
  resetOnHide = false,
}: IAssistantDragArgs) {
  const theme = useTheme();
  const viewportGap = parseFloat(theme.spacing(4));
  const dragThreshold = parseFloat(theme.spacing(1));
  const keyboardStep = parseFloat(theme.spacing(5));

  const [storedPosition, setStoredPosition] =
    useLocalStorage<unknown>(storageKey);
  const [position, setPosition] = useState<IAssistantPosition | undefined>(() =>
    isAssistantPosition(storedPosition) ? storedPosition : undefined
  );
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef<IAssistantDragStart>();
  const shouldSuppressNextClick = useRef(false);

  const finishDrag = useCallback(() => {
    dragStart.current = undefined;
    setIsDragging(false);
  }, []);

  const keepWithinViewport = useCallback(() => {
    const element = elementRef.current;
    if (!element) return;
    setPosition((current) => {
      if (!current) return current;
      const next = clampAssistantPosition(
        current,
        element.getBoundingClientRect(),
        viewportGap
      );
      return next.left === current.left && next.top === current.top
        ? current
        : next;
    });
  }, [elementRef, viewportGap]);

  useEffect(() => {
    if (resetOnHide && !visible) return;
    if (position && !isDragging) setStoredPosition(position);
  }, [isDragging, position, resetOnHide, setStoredPosition, visible]);

  useLayoutEffect(() => {
    if (!visible) {
      finishDrag();
      if (resetOnHide) {
        shouldSuppressNextClick.current = false;
        setPosition(undefined);
        setStoredPosition(null);
      }
      return;
    }

    const observer = new ResizeObserver(keepWithinViewport);
    if (elementRef.current) observer.observe(elementRef.current);
    window.addEventListener("resize", keepWithinViewport);
    keepWithinViewport();
    return () => {
      dragStart.current = undefined;
      observer.disconnect();
      window.removeEventListener("resize", keepWithinViewport);
    };
  }, [
    elementRef,
    finishDrag,
    keepWithinViewport,
    resetOnHide,
    setStoredPosition,
    visible,
  ]);

  const handlePointerDown = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      if (!event.isPrimary || event.button !== 0) return;
      const target = event.target as HTMLElement;
      const button = target.closest("button");
      if (button && button !== event.currentTarget) return;
      const bounds = elementRef.current?.getBoundingClientRect();
      if (!bounds) return;
      event.stopPropagation();
      shouldSuppressNextClick.current = false;
      dragStart.current = {
        pointerId: event.pointerId,
        clientX: event.clientX,
        clientY: event.clientY,
        left: bounds.left,
        top: bounds.top,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [elementRef]
  );

  const handlePointerMove = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      const origin = dragStart.current;
      const element = elementRef.current;
      if (!origin || origin.pointerId !== event.pointerId || !element) return;
      const deltaX = event.clientX - origin.clientX;
      const deltaY = event.clientY - origin.clientY;
      if (
        !shouldSuppressNextClick.current &&
        Math.hypot(deltaX, deltaY) < dragThreshold
      )
        return;
      event.preventDefault();
      event.stopPropagation();
      shouldSuppressNextClick.current = true;
      setIsDragging(true);
      setPosition(
        clampAssistantPosition(
          { left: origin.left + deltaX, top: origin.top + deltaY },
          element.getBoundingClientRect(),
          viewportGap
        )
      );
    },
    [dragThreshold, elementRef, viewportGap]
  );

  const handlePointerCancel = useCallback(() => {
    shouldSuppressNextClick.current = false;
    finishDrag();
  }, [finishDrag]);

  const handleLostPointerCapture = useCallback(() => {
    // After pointer-up, retain suppression for the click generated by the drag.
    if (dragStart.current) shouldSuppressNextClick.current = false;
    finishDrag();
  }, [finishDrag]);

  const handleClickCapture = useCallback((event: MouseEvent<HTMLElement>) => {
    // Clicks without a pointer must still activate the launcher.
    if (event.detail === 0) shouldSuppressNextClick.current = false;
    if (!shouldSuppressNextClick.current) return;
    event.preventDefault();
    event.stopPropagation();
    shouldSuppressNextClick.current = false;
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (event.target !== event.currentTarget) return;
      shouldSuppressNextClick.current = false;
      const bounds = elementRef.current?.getBoundingClientRect();
      if (!bounds) return;
      const next = { left: bounds.left, top: bounds.top };
      switch (event.key) {
        case "ArrowLeft":
          next.left -= keyboardStep;
          break;
        case "ArrowRight":
          next.left += keyboardStep;
          break;
        case "ArrowUp":
          next.top -= keyboardStep;
          break;
        case "ArrowDown":
          next.top += keyboardStep;
          break;
        default:
          return;
      }
      event.preventDefault();
      event.stopPropagation();
      setPosition(clampAssistantPosition(next, bounds, viewportGap));
    },
    [elementRef, keyboardStep, viewportGap]
  );

  const handlers = useMemo<AssistantDragHandlers>(
    () => ({
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: finishDrag,
      onPointerCancel: handlePointerCancel,
      onLostPointerCapture: handleLostPointerCapture,
      onClickCapture: handleClickCapture,
      onKeyDown: handleKeyDown,
    }),
    [
      finishDrag,
      handlePointerDown,
      handlePointerMove,
      handlePointerCancel,
      handleLostPointerCapture,
      handleClickCapture,
      handleKeyDown,
    ]
  );

  const handlePositionChange = useCallback(
    (next: IAssistantPosition) => {
      finishDrag();
      setPosition(next);
    },
    [finishDrag]
  );

  return { position, isDragging, handlers, handlePositionChange };
}
